import { FC, useEffect, useState, useMemo } from 'react';
import {
  SpheraNotificationGroup,
  SpheraNotification,
} from '@sphera-cloud/component-ui-reactjs';
import { Grid, GridSortChangeEvent, ISortDescriptor } from 'lca-common-ui-components';
import { downloadFile, getFileContentByRangesRequest } from 'src/utilities/utilities';
import { File } from 'src/components/FilesList/types';
import { INotificationObj } from 'src/pages/Files/types';
import { useSpinnerContext } from 'src/context/SpinnerContext';
import { useUserSettings } from 'src/hooks/useUserSettings';
import { PAGE_OPTIONS } from 'src/helpers/Constants';
import { TruncateText } from '../TruncateText';
import filesApi from 'src/store/slices/files/api';
import { compareValues } from 'src/helpers/sortHelpers';
interface Props {
  files: any[];
  jobId?: string;
}
const initialPageOptions = {
  skip: 0,
  take: 25,
};

const initialSort: Array<ISortDescriptor> = [{ field: '', dir: 'asc' }];

const defaultNotification = { isOpen: false, type: '', text: '' };

export const FilesGrid: FC<Props> = ({ files}) => {
  const { getFormattedDateTime } = useUserSettings();
  const { setShowSpinner } = useSpinnerContext();
  const [notification, setNotification] = useState(defaultNotification);
  const [allFilesData, setAllFilesData] = useState<File[]>([]);
  const [pageOptions, setPageOptions] = useState<{ skip: number; take: number }>(
    initialPageOptions,
  );
  const [sort, setSort] = useState(initialSort);

  const generateNotificationObj = (type: string, message: string): INotificationObj => {
    return {
      isOpen: true,
      type,
      text: `Error: ${message}`,
    };
  };

  const fetchFileContent = async ({ fileId, name }: File) => {
    try {
      setShowSpinner(true);
      const customerId = sessionStorage.getItem('customerId');
      let endpoint = `files/${fileId}/content`;
      if (customerId) {
        endpoint = `files/${fileId}/content?customerId=${customerId}`;
      }
      const response = await getFileContentByRangesRequest(endpoint);
      downloadFile(response, name);
      setShowSpinner(false);
    } catch (e: any) {
      setNotification(generateNotificationObj('error', e.message));
      setShowSpinner(false);
    }
  };

  const pageChange = (event: { page: { skip: number; take: number } }) => {
    const { skip, take } = event.page;
    setPageOptions({ skip, take });
  };

  const handleSortChange = (e: GridSortChangeEvent) => {
    if (pageOptions.skip !== 0) {
      setPageOptions(prev => ({ ...prev, skip: 0 }));
    }
    setSort(e.sort as ISortDescriptor[]);
  };

  const filesData = allFilesData.map((file) => {
    const actions = [];
    if (file.status === 'Succeeded') {
      actions.push({
        text: 'Download',
        onClick: () => {
          fetchFileContent(file);
        },
        icon: 'icon-ui-general-download',
      });
    }
    return {
      ...file,
      ...files.find((item) => item.fileId === file.fileId),
      actions: actions,
    };
  });

  const splitIntoChunks = (arr: string[], size: number): string[][] => {
    const result: string[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const buildIdsString = (ids: string[]): string => {
    const quoted = ids.map(id => '"' + id + '"').join(",");
    return "[" + quoted + "]";
  };

  const buildParams = (
    ids: string[],
    sortValue: string,
    takeAmount: number,
    page: number,
  ) => ({
    ids: buildIdsString(ids),
    sort: sortValue,
    take: takeAmount,
    page,
  });

   const fetchAllFileChunks = async (
    sortValue: string,
    page: number,
  ) => {
    try {
      const chunkSize = 40;
      const fileIds = files.map(f => f.fileId);
      const chunks = splitIntoChunks(fileIds, chunkSize);

      const requests = chunks.map(chunk =>
        filesApi.fetchFiles(buildParams(chunk, sortValue, chunk.length, page)));

      const responses = await Promise.all(requests);
      const allFiles = responses.flatMap(r => r.data.items);

      setAllFilesData(allFiles);
    } catch {
      setNotification(generateNotificationObj("error", "Failed to list files"));
    }
  };

  useEffect(() => {
    if (!files?.length) return;
    const sortField = sort[0]?.field;
    const sortValue = sortField ? `${sortField} ${sort[0].dir}` : "";

    fetchAllFileChunks(sortValue, 1);
  }, [files]);

  const processedData = useMemo(() => {
    if (!filesData) return [];
    const data = [...filesData];
    const sortField = sort[0]?.field;
    const sortDir = sort[0]?.dir;

    if (sortField && sortDir) {
      data.sort((a, b) =>
        compareValues(a[sortField as keyof typeof a], b[sortField as keyof typeof b], sortDir));
    }
    return data;
  }, [filesData, pageOptions]);

  const slicedProcessedData = processedData.slice(pageOptions.skip, pageOptions.skip + pageOptions.take);

  const renderDate = ({ dataItem }: { dataItem: File }) => {
    const date = getFormattedDateTime(dataItem.creationDatetime);
    return <td>
      <TruncateText text={date ?? ''} />
    </td>;
  };

  const extendedData = slicedProcessedData.map((item) => ({
    ...item,
    _uploadedOn: (renderDate({ dataItem: item }) as any)?.props?.children,
  }));

  const metadata = useMemo(
    () => [
      { field: 'name', title: 'Name', sortable: true },
      { field: 'creationDatetime', title: 'Uploaded on', type: 'component', object: '_uploadedOn', width: 260, filterable: false, sortable: true },
      { field: 'fileType', title: 'File Type', sortable: true },
    ],
    [getFormattedDateTime],
  );

  return (
    <>
      <Grid
        serverSideData
        pageable
        scrollable="scrollable"
        data={extendedData}
        metadata={metadata}
        total={processedData.length}
        sort={sort}
        pageOptions={PAGE_OPTIONS}
        onPageChange={pageChange}
        {...pageOptions}
        onSortChange={handleSortChange}
        actions={[
          {
            text: 'Download',
            onClick: (file: File) => {
              fetchFileContent(file);
            },
            icon: 'icon-ui-general-download',
          },
        ]}
        maxActions={2}
      />
      <SpheraNotificationGroup
        position={{
          top: 70,
          right: 30,
        }}
      >
        {notification.isOpen && (
          <SpheraNotification
            closable={true}
            type={notification.type}
            text={notification.text}
            onClose={() => setNotification(defaultNotification)}
          />
        )}
      </SpheraNotificationGroup>
    </>
  );
};
